import "styles/print.css";
import React, { useState, useEffect } from "react";
import ThaiBahtText from "thai-baht-text";
import { NumericFormat } from "react-number-format";
import dayjs from "dayjs";

function fmt(v) {
  if (v == null) return "0.00";
  return new Intl.NumberFormat("th-TH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(v));
}

function formatThaiDate(dateStr) {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear() + 543;
  return `${day}/${month}/${year}`;
}

/**
 * แปลง invoice.items เป็น document-level rows
 * แต่ละ invoice item → 1 แถว (เลขที่เอกสาร, วันที่, จำนวนรายการ, มูลค่าก่อนภาษี, ภาษี, สุทธิ)
 */
function mapInvoiceToDocumentRows(invoice) {
  const items = invoice?.items ?? [];

  if (items.length === 0) {
    return [
      {
        documentNumber: invoice?.document_no ?? "-",
        documentDate: "",
        quantity: 1,
        taxable_amount:
          (Number(invoice?.taxable_amount_has_vat) || 0) +
          (Number(invoice?.taxable_amount_zero_vat) || 0) +
          (Number(invoice?.taxable_amount_exempt) || 0),
        vat: Number(invoice?.vat_amount) || 0,
        total_amount: Number(invoice?.grand_total) || 0,
        remark: "",
      },
    ];
  }

  return items.map((item) => {
    const bill = item.memberDebtTransaction?.payment?.bill;
    return {
      documentNumber: bill?.receipt_no ?? "",
      documentDate: bill?.created_at ?? "",
      quantity: item.quantity ?? 1,
      taxable_amount: Number(item.taxable_amount) || 0,
      vat: Number(item.vat) || 0,
      total_amount: Number(item.total_amount) || 0,
      remark: item.remark ?? "",
    };
  });
}

export function PrintINV({ innerRef, invoice }) {
  const maxRowPerPage = 10;
  const [prow, setProw] = useState([]);
  const [tmpRow, setTmpRow] = useState(0);

  useEffect(() => {
    if (!invoice) return;

    const rows = mapInvoiceToDocumentRows(invoice);
    const p = Math.ceil(rows.length / maxRowPerPage) || 1;
    const arr = [];
    for (let i = 1; i <= p; i++) {
      const dataPage = rows.slice((i - 1) * maxRowPerPage, i * maxRowPerPage);
      if (i === p && dataPage.length < maxRowPerPage) {
        setTmpRow(maxRowPerPage - dataPage.length);
      }
      arr.push(dataPage);
    }
    setProw(arr);
  }, [invoice]);

  if (!invoice) return <div />;

  const allRows = mapInvoiceToDocumentRows(invoice);
  const totalQuantity = allRows.reduce((s, r) => s + (Number(r.quantity) || 0), 0);
  const totalTaxable = allRows.reduce((s, r) => s + r.taxable_amount, 0);
  const totalVat = allRows.reduce((s, r) => s + r.vat, 0);
  const totalAmount = allRows.reduce((s, r) => s + r.total_amount, 0);

  return (
    <div ref={innerRef}>
      {prow.map((pageData, index) => (
        <PagePrint
          key={index}
          pageCur={index + 1}
          pageAll={prow.length}
          pdata={pageData}
          tmpRow={tmpRow}
          maxRowPerPage={maxRowPerPage}
          invoice={invoice}
          totalQuantity={totalQuantity}
          totalTaxable={totalTaxable}
          totalVat={totalVat}
          totalAmount={totalAmount}
        />
      ))}
    </div>
  );
}

const PagePrint = ({
  pageCur,
  pageAll,
  pdata,
  tmpRow,
  maxRowPerPage,
  invoice,
  totalQuantity,
  totalTaxable,
  totalVat,
  totalAmount,
}) => {
  const business = invoice?.business ?? {};
  const member = invoice?.member ?? {};
  const creator = invoice?.createdByUserBusinessGroup ?? {};

  // ─── Business info ────────────────────────────────────────────
  const companyName = business.company_name || "-";
  const businessAddress = business.address || "-";
  const businessTaxNo = business.tax_number || "-";
  const businessPhone =
    business.contact_phone_number || business.contact_tel || "-";
  const businessEmail = business.contact_email || "";
  const businessLogo = business.image_path || "/images/logo1.png";

  // ─── Member info ──────────────────────────────────────────────
  const memberName =
    `${member.first_name_th || ""} ${member.last_name_th || ""}`.trim() ||
    member.company_name_th ||
    member.code ||
    "-";
  const memberAddress = member.address || "-";
  const memberPhone = member.phone_number || "-";
  const memberTaxNo = member.tax_number || "-";

  // ─── Document info ────────────────────────────────────────────
  const documentNo = invoice?.document_no || "-";
  const issueDate = invoice?.issue_date
    ? dayjs(invoice.issue_date).format("DD/MM/YYYY")
    : "-";
  const dueDate = invoice?.due_date ? formatThaiDate(invoice.due_date) : "-";
  const reference = invoice?.reference || "-";
  const isTaxInvoice = invoice?.is_tax_invoice ?? false;
  const creatorName =
    `${creator.first_name || ""} ${creator.last_name || ""}`.trim() || "-";

  // ─── Summary ──────────────────────────────────────────────────
  const amountBeforeDiscount = Number(invoice?.amount_before_discount) || 0;
  const discountAmount = Number(invoice?.discount_amount) || 0;
  const amountAfterDiscount = Number(invoice?.amount_after_discount) || 0;
  const taxableHasVat = Number(invoice?.taxable_amount_has_vat) || 0;
  const taxableZero = Number(invoice?.taxable_amount_zero_vat) || 0;
  const taxableExempt = Number(invoice?.taxable_amount_exempt) || 0;
  const vatAmount = Number(invoice?.vat_amount) || 0;
  const grandTotal = Number(invoice?.grand_total) || 0;
  const hasDiscount = discountAmount > 0;

  // ─── NV/V Breakdown ───────────────────────────────────────────
  const nvTaxExcluded = taxableExempt;
  const nvTotal = taxableExempt;
  const vTaxExcluded = taxableHasVat + taxableZero;
  const vTotal = taxableHasVat + taxableZero + vatAmount;
  const totalBeforeTax = taxableExempt + taxableHasVat + taxableZero;

  return (
    <div
      className="text-[10.5px] text-[#2C2C2C] mb-1"
      style={{
        width: "210mm",
        height: "297mm",
        padding: "5px 30px",
        margin: "0",
        background: "white",
        position: "relative",
      }}
    >
      {/* ─── Header ─────────────────────────────────────────── */}
      <div className="flex text-[10.5px] items-end mb-3">
        <div className="flex items-start mr-auto mt-3">
          <div className="mr-[15px]">
            <img
              src={businessLogo}
              className="w-[58px] print-logo"
              alt="logo"
            />
          </div>
          <div>
            <div className="font-bold">{companyName}</div>
            <div>{businessAddress}</div>
            <div>เลขผู้เสียภาษี / Tax ID {businessTaxNo}</div>
            <div>
              <span className="mr-2">Tel: {businessPhone}</span>
              {businessEmail && <span>E-mail: {businessEmail}</span>}
            </div>
          </div>
        </div>
        <div>
          <div className="text-right mb-3">
            แผ่นที่ {pageCur}/{pageAll}
          </div>
          <div className="flex items-center">
            {/* <img src="/images/qrcode.png" className="w-[56px] mr-4" alt="qr" /> */}
            <div className="rounded-sm border text-center overflow-hidden">
              <div className="text-[14px] py-1 text-white bg-orange-500">
                {isTaxInvoice ? (
                  <>
                    ใบแจ้งหนี้ /<br />
                    ใบกำกับภาษี
                  </>
                ) : (
                  "ใบแจ้งหนี้"
                )}
              </div>
              <div className="px-2 py-1">
                <span>เลขที่เอกสาร : </span>
                <span className="font-bold">{documentNo}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ─── Customer & Document info ────────────────────────── */}
      <div className="flex items-end">
        <div className="grow pr-[20px]">
          <div className="flex mb-2">
            <div className="flex-none w-[130px] pr-3">
              ข้อมูลลูกค้า/Customer
            </div>
            <div className="grow text-[#000] font-bold">{memberName}</div>
          </div>
          <div className="flex mb-2">
            <div className="flex-none w-[130px] text-right pr-3">
              ที่อยู่/Address
            </div>
            <div className="grow text-[#000] font-bold">{memberAddress}</div>
          </div>
          <div className="flex mb-2">
            <div className="flex-none w-[130px] text-right pr-3">
              เบอร์โทรผู้รับ/Phone
            </div>
            <div className="grow text-[#000] font-bold">{memberPhone}</div>
          </div>
          <div className="flex mb-2">
            <div className="flex-none w-[130px] text-right pr-3">
              เลขประจำตัวผู้เสียภาษี
            </div>
            <div className="grow text-[#000] font-bold">{memberTaxNo}</div>
          </div>
        </div>
        <div className="flex-none mr-4">
          <div className="flex mb-2">
            <div className="flex-none w-[80px] mr-3 text-end">
              วันที่ออกเอกสาร
            </div>
            <div className="grow text-[#000] font-bold">{issueDate}</div>
          </div>
          <div className="flex mb-2">
            <div className="flex-none w-[80px] mr-3 text-end">
              วันที่ครบกำหนด
            </div>
            <div className="grow text-[#000] font-bold">{dueDate}</div>
          </div>
          <div className="flex mb-2">
            <div className="flex-none w-[80px] mr-3 text-end">
              เงื่อนไขชำระเงิน
            </div>
            <div className="grow text-[#000] font-bold">
              {invoice?.payment_term_name || "-"}
            </div>
          </div>
        </div>
        <div className="flex-none">
          <div className="flex mb-2">
            <div className="flex-none w-[70px] mr-3">อ้างอิง</div>
            <div className="grow text-[#000] font-bold">{reference}</div>
          </div>
          <div className="flex mb-2">
            <div className="flex-none w-[70px] mr-3">&nbsp;</div>
            <div className="grow" />
          </div>
          <div className="flex mb-2">
            <div className="flex-none w-[70px] mr-3">ผู้จัดทำเอกสาร</div>
            <div className="grow text-[#000] font-bold">{creatorName}</div>
          </div>
        </div>
      </div>

      {/* ─── Items table (document-level) ────────────────────── */}
      <div className="py-2">
        <table className="w-full border-collapse border-slate-700 table-print">
          <thead className="bg-[#FFE5CC]">
            <tr>
              <th
                width="4%"
                className="border-x border-y text-center font-normal"
              >
                ที่
              </th>
              <th
                width="22%"
                className="border-x border-y text-center font-normal"
              >
                เลขที่เอกสาร
              </th>
              <th
                width="14%"
                className="border-x border-y text-center font-normal"
              >
                วันที่เอกสาร
              </th>
              <th
                width="10%"
                className="border-x border-y text-center font-normal"
              >
                จำนวนรายการ
              </th>
              <th
                width="14%"
                className="border-x border-y text-center font-normal"
              >
                มูลค่าก่อนภาษี
              </th>
              <th
                width="12%"
                className="border-x border-y text-center font-normal"
              >
                มูลค่าภาษี
              </th>
              <th
                width="14%"
                className="border-x border-y text-center font-normal"
              >
                มูลค่าสุทธิ
              </th>
              <th
                width="10%"
                className="border-x border-y text-center font-normal"
              >
                หมายเหตุ
              </th>
            </tr>
          </thead>
          <tbody>
            {pdata.map((item, i) => (
              <tr style={{ height: "30px" }} key={i}>
                <td className="border-x ellipsis text-center">
                  {(pageCur - 1) * maxRowPerPage + (i + 1)}
                </td>
                <td className="border-x pl-1 ellipsis">
                  {item.documentNumber}
                </td>
                <td className="border-x text-center ellipsis">
                  {item.documentDate ? formatThaiDate(item.documentDate) : ""}
                </td>
                <td className="border-x text-end pr-1 ellipsis">
                  <NumericFormat
                    displayType="text"
                    thousandSeparator=","
                    decimalScale={0}
                    value={item.quantity}
                  />
                </td>
                <td className="border-x text-end pr-1 ellipsis">
                  {fmt(item.taxable_amount)}
                </td>
                <td className="border-x text-end pr-1 ellipsis">
                  {fmt(item.vat)}
                </td>
                <td className="border-x text-end pr-1 ellipsis">
                  {fmt(item.total_amount)}
                </td>
                <td className="border-x pl-1 ellipsis" style={{ maxWidth: "70px" }}>
                  <div className="text-[9px]">{item.remark}</div>
                </td>
              </tr>
            ))}

            {pageCur === pageAll &&
              [...Array(tmpRow)].map((_, i) => (
                <tr style={{ height: "30px" }} key={`tmp${i}`}>
                  {[...Array(8)].map((_, j) => (
                    <td key={j} className="border-x ellipsis" />
                  ))}
                </tr>
              ))}
          </tbody>
          <tfoot>
            <tr style={{ height: "30px" }}>
              <td
                className="border-x border-y text-center font-semibold"
                colSpan={3}
              >
                รวม
              </td>
              <td className="border-x border-y text-end pr-1 font-semibold">
                <NumericFormat
                  displayType="text"
                  thousandSeparator=","
                  decimalScale={0}
                  value={totalQuantity}
                />
              </td>
              <td className="border-x border-y text-end pr-1 font-semibold">
                {fmt(totalTaxable)}
              </td>
              <td className="border-x border-y text-end pr-1 font-semibold">
                {fmt(totalVat)}
              </td>
              <td className="border-x border-y text-end pr-1 font-semibold">
                {fmt(totalAmount)}
              </td>
              <td className="border-x border-y" />
            </tr>
          </tfoot>
        </table>
      </div>

      {/* ─── Footer Row 1: หมายเหตุ + NV/V | ยอดรวม ──────────── */}
      <div className="grid grid-cols-2 gap-2 text-[#000]">
        {/* Left: remark + NV/V breakdown */}
        <div>
          <div className="mb-2">หมายเหตุ</div>
          <div className="rounded-lg border px-4 min-h-[70px]">
            {invoice?.remark && (
              <div className="pt-2 text-[10px]">{invoice.remark}</div>
            )}
          </div>

          {/* NV/V Breakdown */}
          <div className="pt-3">
            <div className="grid grid-cols-4 gap-1 mb-[7px]">
              <div></div>
              <div className="text-end text-[9px]">มูลค่าก่อนภาษี</div>
              <div className="text-end text-[9px]">ภาษีมูลค่าเพิ่ม</div>
              <div className="text-end text-[9px]">ราคารวม VAT</div>
            </div>
            <div className="grid grid-cols-4 gap-1 mb-[7px]">
              <div>NV สินค้าไม่มีภาษี</div>
              <div className="text-end pr-2">{fmt(nvTaxExcluded)}</div>
              <div className="text-end pr-2">{fmt(0)}</div>
              <div className="text-end pr-2">{fmt(nvTotal)}</div>
            </div>
            <div className="grid grid-cols-4 gap-1 mb-[7px]">
              <div>V สินค้าที่มีภาษี 7%</div>
              <div className="text-end pr-2">{fmt(vTaxExcluded)}</div>
              <div className="text-end pr-2">{fmt(vatAmount)}</div>
              <div className="text-end pr-2">{fmt(vTotal)}</div>
            </div>
            <div className="grid grid-cols-4 gap-1 mb-[7px] font-semibold">
              <div>มูลค่าสินค้ารวม</div>
              <div className="text-end pr-2">{fmt(totalBeforeTax)}</div>
              <div className="text-end pr-2">{fmt(vatAmount)}</div>
              <div className="text-end pr-2">{fmt(grandTotal)}</div>
            </div>
          </div>
        </div>

        {/* Right: summary */}
        <div className="pl-[30px]">
          {hasDiscount && (
            <>
              <div className="grid grid-cols-12 gap-2 mb-[7px] text-end">
                <div className="col-span-7">มูลค่าสินค้ารวมภาษี</div>
                <div className="col-span-3">{fmt(amountBeforeDiscount)}</div>
                <div className="col-span-2">บาท</div>
              </div>
              <div className="grid grid-cols-12 gap-2 mb-[7px] text-end">
                <div className="col-span-7">ส่วนลด</div>
                <div className="col-span-3">{fmt(discountAmount)}</div>
                <div className="col-span-2">บาท</div>
              </div>
              <div className="grid grid-cols-12 gap-2 mb-[7px] text-end">
                <div className="col-span-7">มูลค่าหลังหักส่วนลด</div>
                <div className="col-span-3">{fmt(amountAfterDiscount)}</div>
                <div className="col-span-2">บาท</div>
              </div>
              <div className="py-1">
                <hr />
              </div>
            </>
          )}

          {taxableExempt > 0 && (
            <div className="grid grid-cols-12 gap-2 mb-[7px] text-end">
              <div className="col-span-7">มูลค่าสินค้าไม่มีภาษี</div>
              <div className="col-span-3">{fmt(taxableExempt)}</div>
              <div className="col-span-2">บาท</div>
            </div>
          )}

          {taxableZero > 0 && (
            <div className="grid grid-cols-12 gap-2 mb-[7px] text-end">
              <div className="col-span-7">มูลค่าสินค้า VAT 0%</div>
              <div className="col-span-3">{fmt(taxableZero)}</div>
              <div className="col-span-2">บาท</div>
            </div>
          )}

          <div className="grid grid-cols-12 gap-2 mb-[7px] text-end">
            <div className="col-span-7">มูลค่าสินค้าก่อนภาษี</div>
            <div className="col-span-3">{fmt(taxableHasVat)}</div>
            <div className="col-span-2">บาท</div>
          </div>

          <div className="grid grid-cols-12 gap-2 mb-[7px] text-end">
            <div className="col-span-7">ภาษีมูลค่าเพิ่ม/VAT</div>
            <div className="col-span-3">{fmt(vatAmount)}</div>
            <div className="col-span-2">บาท</div>
          </div>

          <div className="py-2 bg-[#fceadc] text-[11.5px] mb-2">
            <div className="grid grid-cols-12 gap-2 text-end">
              <div className="col-span-7">มูลค่ารวมสุทธิ/NET AMOUNT</div>
              <div className="col-span-3 font-bold">{fmt(grandTotal)}</div>
              <div className="col-span-2">บาท</div>
            </div>
          </div>

          <div className="text-end text-[11.5px] text-[#000] font-semibold">
            ( {ThaiBahtText(grandTotal.toFixed(2).replace(/,/g, ""))} )
          </div>
        </div>
      </div>

      {/* ─── Footer Row 2: ช่องทางการชำระเงิน | ลายเซ็น ────────── */}
      <div className="grid grid-cols-2 gap-2 text-[#000] pt-4">
        {/* Left: ช่องทางการชำระเงิน */}
        <div>
          {invoice?.business?.settingBranchBankSetting &&
            (() => {
              const bankSetting = invoice.business.settingBranchBankSetting;
              return (
                <>
                  <div className="text-[11px] font-semibold mb-2">
                    ช่องทางการชำระเงิน
                  </div>
                  <div className="flex items-start gap-2">
                    {bankSetting.bank?.image_path ? (
                      <img
                        src={bankSetting.bank.image_path}
                        width="28"
                        alt={bankSetting.bank.name}
                        className="mt-[2px] flex-none"
                      />
                    ) : (
                      <div className="w-[28px] h-[28px] bg-gray-200 rounded flex-none" />
                    )}
                    <div className="text-[9.5px]">
                      <div>
                        {bankSetting.bank?.name ?? "-"}
                        {bankSetting.branch?.branch_name
                          ? ` (${bankSetting.branch.branch_name})`
                          : ""}
                      </div>
                      <div className="font-bold text-[#2DC76D]">
                        {bankSetting.account_number}
                      </div>
                      <div>{bankSetting.account_name}</div>
                    </div>
                  </div>
                </>
              );
            })()}
        </div>

        {/* Right: Signatures */}
        <div className="pl-[30px]">
          <div className="text-[11.5px]">
            <div className="grid grid-cols-3 gap-3">
              <div className="flex items-center justify-center flex-col">
                <div className="h-[20px] border-b w-[100px] border-dashed border-black mb-2" />
                <div className="mb-2">วันที่ _____/_____/_____</div>
                <div>ผู้จัดทำ</div>
              </div>
              <div className="flex items-center justify-center flex-col">
                <div className="h-[20px] border-b w-[100px] border-dashed border-black mb-2" />
                <div className="mb-2">วันที่ _____/_____/_____</div>
                <div>ผู้อนุมัติ</div>
              </div>
              <div className="flex items-center justify-center flex-col">
                <div className="h-[20px] border-b w-[100px] border-dashed border-black mb-2" />
                <div className="mb-2">วันที่ _____/_____/_____</div>
                <div>ลูกค้า</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
